/**
 * Shared pure helpers lifted out of `app/(main)/products/ProductsClient.tsx`
 * during the task #16 split. Every ProductCard / ProductDetail /
 * FilterSections file reaches in here so none of them re-declare the same
 * tiny helpers.
 *
 * Source lines in the pre-split file: 60–122, 501–520.
 */

import type React from "react";

// Re-exported from the single source (lib/media.ts) so this module's existing
// consumers keep importing from here. See audit #16.
export { VIDEO_EXTENSIONS, isVideoKey } from "@/lib/media";

/**
 * Category-aware availability chip for product cards + PDP. Combines the lead
 * time (deliveryTiming) with geography into ONE clear label, because the two
 * axes only make sense together:
 *   - Fresh is Leeds-only (same-day delivery OR collection), so same-day reads
 *     "Same-day · Leeds"; a made-ahead fresh piece reads "Pre-order · Leeds".
 *   - Crochet/Preserved are stock-in-hand but SHIPPED UK-wide (not same-day),
 *     so `sameDayOk` reads "In stock · UK-wide" — never "same-day", which would
 *     over-promise UK delivery speed.
 *   - enquireOnly (bespoke, no online checkout) reads "Enquire".
 *   - inStock < 1 reads "Sold out" — see the precedence note below.
 * `tone` drives the chip colour: now = ready to buy, ahead = plan ahead.
 *
 * `inStock` is OPTIONAL and backwards-compatible: callers that don't pass it
 * (the product cards) behave exactly as before.
 */
export type AvailabilityTone = "now" | "ahead" | "enquire";
export function availabilityBadge(
  category: string,
  deliveryTiming?: string,
  inStock?: number | null,
): { label: string; tone: AvailabilityTone } | null {
  if (deliveryTiming === "enquireOnly") return { label: "Enquire", tone: "enquire" };
  // Sold out OUTRANKS every lead-time/geography label: the item can't be bought
  // at all, so "Same-day · Leeds" would be an outright lie next to a disabled
  // buy box. Precedence (enquire → out of stock → timing) matches
  // `getProductAvailability` in lib/product-availability.ts, and the label
  // matches the quick-view drawer's "Sold out" button (ProductDetail.tsx).
  // Tone deliberately reuses the muted "enquire" tone rather than adding a new
  // member to AvailabilityTone: that union is consumed as an exhaustive
  // `Record<AvailabilityTone, string>` in the admin products client, so a new
  // tone would break that surface.
  if (inStock != null && inStock < 1) return { label: "Sold out", tone: "enquire" };
  const geo =
    category === "Fresh"
      ? "Leeds"
      : category === "Crochet" || category === "Preserved"
        ? "UK-wide"
        : null;
  if (deliveryTiming === "advanceOnly") {
    return { label: geo ? `Pre-order · ${geo}` : "Pre-order", tone: "ahead" };
  }
  // sameDayOk (and the notNull default) — "ready now"
  const timing = category === "Fresh" ? "Same-day" : "In stock";
  return { label: geo ? `${timing} · ${geo}` : timing, tone: "now" };
}

/** Tailwind classes for an availability chip, keyed by tone. */
export const AVAILABILITY_TONE_CLASS: Record<AvailabilityTone, string> = {
  now: "bg-primary/10 text-primary",
  ahead: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200",
  enquire: "bg-muted text-muted-foreground",
};

export const OCCASIONS = [
  "Birthday",
  "Sympathy",
  "Get Well",
  "Congratulations",
  "Graduation",
  "Thank You",
  "Anniversary",
  "Just Because",
  "Creative Gifts",
] as const;

/** Single hex or array for gradient (e.g. ["#ec4899", "#dc2626"]). */
export const COLOUR_HEX: Record<string, string | string[]> = {
  Pink: "#ec4899",
  Red: "#dc2626",
  White: "#fafafa",
  Cream: "#fef3c7",
  Yellow: "#eab308",
  Orange: "#f97316",
  Purple: "#a855f7",
  Blue: "#3b82f6",
  Green: "#22c55e",
  Neutral: "#78716c",
  // Multi-colour examples (gradients):
  "Pink-Red": ["#ec4899", "#dc2626"],
  "Orange-Yellow": ["#f97316", "#eab308"],
  "Blue-Purple": ["#3b82f6", "#a855f7"],
};

/** Returns an inline style for a colour swatch — solid fill or gradient. */
export function getColourStyle(name: string): React.CSSProperties {
  const val = COLOUR_HEX[name];
  if (Array.isArray(val) && val.length >= 2) {
    const stops = val.join(", ");
    return {
      backgroundImage: `linear-gradient(135deg, ${stops})`,
      backgroundColor: "transparent",
    };
  }
  const hex = typeof val === "string" ? val : val?.[0] ?? "#94a3b8";
  return { backgroundColor: hex };
}

export function categoryDisplayLabel(category: string): string {
  // Display the category name as-is. (Previously mapped "Preserved" →
  // "Preserved/Permanent"; we've standardised on "Preserved" and dropped
  // the "Permanent" wording — see also the service titles.)
  return category;
}

export function stripEmoji(str: string): string {
  return str
    .replace(/[\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]/gu, "")
    .replace(/\s+/g, " ")
    .trim();
}

/** Parse "Colours: Red, White" from description and return colour names. */
export function parseColoursFromDescription(desc: string | null): string[] {
  if (!desc) return [];
  const match = desc.match(/Colours?:\s*([^.]+)/i);
  if (!match) return [];
  return match[1]
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

/**
 * Resolve the public URL for a gallery image key. Prefers the R2 raw URL
 * (already baked by the list API) and falls back to the in-worker proxy.
 */
export function imageUrlForKey(key: string, rawUrl?: string): string {
  return rawUrl || `/api/gallery/image?key=${encodeURIComponent(key)}`;
}

/** Short preview of a product description, with "Colours: …" stripped. */
export function descriptionPreview(desc: string | null): string {
  if (!desc) return "";
  return (
    desc
      .replace(/\s*Colours?:\s*[^.]+\.?\s*/i, "")
      .trim()
      .slice(0, 120) || ""
  );
}
